import React, { useMemo } from 'react';

import { Table, Tooltip } from '@bringg/react-components';
import { BringgIcon, BringgFontIcons } from '@bringg/bringg-icons';

import CsvImportColumnsPopover, {
	Props as PopoverProps
} from '../csv-import-columns-popover/csv-import-columns-popover';

import './csv-import-mapping-table.scss';

export type Props = {
	parsedFile: string[][];
	headers: (string | null)[];
	onChange?(headers: (string | null)[]): void;
	failures?: { title: string }[];
	initiallyExpanded?: string[];
	readonly?: boolean;
	translations: PopoverProps['translations'];
	treeData: PopoverProps['treeData'];
	columnsByDataIndex: PopoverProps['columnsByDataIndex'];
};

function CsvImportMappingTable({
	parsedFile,
	headers,
	onChange,
	failures = [],
	initiallyExpanded = [],
	readonly = false,
	translations,
	treeData,
	columnsByDataIndex
}: Props) {
	const columns = useMemo(() => {
		if (!parsedFile) {
			return;
		}

		const selectColumns =
			(index: number): PopoverProps['onSelect'] =>
			(_, e) => {
				const newColumns: (string | null)[] = [...headers];
				newColumns[index] = e.selected ? e.node.key.toString() : null;
				onChange?.(newColumns);
			};

		const fileColumns = parsedFile[0].map((column, index) => ({
			title: (
				<CsvImportColumnsPopover
					readonly={readonly}
					onSelect={selectColumns(index)}
					originalTitle={column}
					headers={headers}
					selected={headers[index]}
					initiallyExpanded={initiallyExpanded}
					translations={translations}
					treeData={treeData}
					columnsByDataIndex={columnsByDataIndex}
				>
					{headers[index] || ''}
				</CsvImportColumnsPopover>
			),
			dataIndex: index,
			key: index,
			...(headers[index] ? { className: 'csv-import-mapping-table-matched' } : {})
		}));

		if (failures.length) {
			fileColumns.unshift({
				title: <></>,
				dataIndex: -1,
				key: -1
			});
		}

		return fileColumns;
	}, [
		parsedFile,
		failures.length,
		headers,
		onChange,
		readonly,
		initiallyExpanded,
		translations,
		treeData,
		columnsByDataIndex
	]);

	const dataSource = useMemo(() => {
		if (!parsedFile) {
			return;
		}

		const fileSource = parsedFile.slice(1).map((cell, key) => ({
			key: key,
			...cell.reduce(
				(acc, value, index) => ({
					...acc,
					[index]: value?.toString() || null
				}),
				{}
			)
		}));

		if (failures.length) {
			return fileSource.map((row, index) => ({
				// add tooltip with failure title before other columns
				[-1]: (
					<Tooltip title={failures[index].title}>
						<BringgIcon iconName={BringgFontIcons.Info} />
					</Tooltip>
				),
				...row
			}));
		}

		return fileSource;
	}, [parsedFile, failures]);

	if (!parsedFile) {
		return null;
	}

	return <Table scroll={{ x: 878 }} className="csv-import-mapping-table" dataSource={dataSource} columns={columns} />;
}

export default CsvImportMappingTable;
